import React, { useState } from 'react';
import { GlitchText, TerminalWindow, TypeBadge, CodeBlock, TestCasesList, RequirementsBlock } from './TerminalUI';
import { challengeTypes } from '../data/challenges';
import { useAIHints } from '../hooks/useAIHints';

/**
 * Intro/Title Screen Component
 */
export const IntroScreen = ({ bootSequence, onStart }) => {
  return (
    <TerminalWindow title="[ TEST DETECTIVE v1.0 ]">
      <div className="intro-screen">
        {bootSequence === 0 && (
          <>
            {/* Clean, readable title */}
            <div className="title-section">
              <h1 className="main-title">TEST DETECTIVE</h1>
              <div className="title-underline"></div>
            </div>
            
            <div className="intro-text">
              <p className="tagline"><GlitchText>// QA INVESTIGATION CHALLENGE //</GlitchText></p>
              <div className="mission-brief">
                <p>► 10 CASES TO INVESTIGATE</p>
                <p>► FIND BUGS, GAPS, AND WEAK TESTS</p>
                <p>► BEAT THE CLOCK FOR BONUS POINTS</p>
              </div>
              <p className="author">[ CREATED BY JEFF HEYTOW ]</p>
            </div>
            
            <button className="start-btn" onClick={onStart}>
              <span className="btn-text">► START INVESTIGATION _</span>
              <span className="btn-glow" />
            </button>
          </>
        )}
        
        {bootSequence >= 1 && (
          <div className="boot-sequence">
            <p className="boot-line">► Loading case files... OK</p>
            {bootSequence >= 2 && <p className="boot-line">► Initializing analysis engine... OK</p>}
            {bootSequence >= 3 && <p className="boot-line">► Establishing secure connection... OK</p>}
            {bootSequence >= 4 && <p className="boot-line success">► SYSTEM READY. LAUNCHING...</p>}
          </div>
        )}
      </div>
    </TerminalWindow>
  );
};

/**
 * AI Hint Section Component
 */
const AIHintSection = ({ challenge, disabled }) => {
  const { canUseAI, usesRemaining, isLoading, getHint, getTimeUntilReset, dailyLimit } = useAIHints();
  const [hint, setHint] = useState(null);
  const [error, setError] = useState(null);

  const handleGetHint = async () => {
    setError(null);
    const result = await getHint(challenge);
    if (result.success) {
      setHint(result.hint);
    } else {
      setError(result.error);
    }
  };

  // Don't show AI section if already answered and no hint was retrieved
  if (disabled && !hint) return null;

  return (
    <div className="ai-section">
      <div className="ai-header">
        <span className={`ai-status ${canUseAI ? 'available' : 'depleted'}`}>
          {canUseAI 
            ? `🤖 AI HINTS: ${usesRemaining}/${dailyLimit} REMAINING`
            : `🤖 HINTS DEPLETED - RESETS IN ${getTimeUntilReset()}`
          }
        </span>
        {!hint && !disabled && (
          <button 
            className={`hint-btn ${isLoading ? 'loading' : ''}`}
            onClick={handleGetHint}
            disabled={!canUseAI || isLoading}
          >
            {isLoading ? '[ ANALYZING... ]' : '[ GET HINT ]'}
          </button>
        )}
      </div>
      
      {hint && (
        <div className="hint-display">
          💡 {hint}
        </div>
      )}
      
      {error && (
        <div className="ai-error">
          ⚠ {error}
        </div>
      )}
    </div>
  );
};

/**
 * Popup Alert Component - Single "Got It!" button only
 */
const PopupAlert = ({ challenge, isCorrect, onDismiss }) => {
  return (
    <div className="popup-overlay">
      <div className={`popup-alert ${isCorrect ? 'correct' : 'incorrect'}`}>
        {/* Header */}
        <div className={`popup-header ${isCorrect ? 'correct' : 'incorrect'}`}>
          <span className={`popup-result ${isCorrect ? 'correct' : 'incorrect'}`}>
            {isCorrect ? '[ ✓ CORRECT ]' : '[ ✗ INCORRECT ]'}
          </span>
        </div>
        
        {/* Body */}
        <div className="popup-body">
          <p className="popup-explanation">{challenge.explanation}</p>
          
          <div className="popup-takeaway">
            <span className="popup-takeaway-label">KEY TAKEAWAY:</span>
            <p className="popup-takeaway-text">{challenge.takeaway}</p>
          </div>
        </div>
        
        {/* Footer - Single button */}
        <div className="popup-footer">
          <button className="popup-btn primary" onClick={onDismiss}>
            GOT IT!
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Main Game Screen Component
 */
export const GameScreen = ({ 
  currentLevel, 
  totalLevels, 
  challenge, 
  score, 
  timeLeft, 
  selectedAnswer, 
  showExplanation,
  onAnswer,
  onNext
}) => {
  const [showPopup, setShowPopup] = useState(false);
  const [hasSeenExplanation, setHasSeenExplanation] = useState(false);

  // Show popup when explanation should be shown
  React.useEffect(() => {
    if (showExplanation && !hasSeenExplanation) {
      setShowPopup(true);
    }
  }, [showExplanation, hasSeenExplanation]);

  // Reset state when level changes
  React.useEffect(() => {
    setShowPopup(false);
    setHasSeenExplanation(false);
  }, [currentLevel]);

  const handleDismissPopup = () => {
    setShowPopup(false);
    setHasSeenExplanation(true);
  };

  const isCorrect = selectedAnswer === challenge.correct;
  const hasAnswered = selectedAnswer !== null;

  return (
    <>
      <TerminalWindow title="[ TEST DETECTIVE v1.0 ]">
        {/* Status Bar */}
        <div className="status-bar">
          <div className="status-left">
            <span className="level-indicator">CASE {currentLevel + 1}/{totalLevels}</span>
            <TypeBadge type={challenge.type} config={challengeTypes} />
            <span className={`difficulty ${challenge.difficulty}`}>
              [{challenge.difficulty.toUpperCase()}]
            </span>
          </div>
          <div className="status-right">
            <span className="score-display">SCORE: {score.toLocaleString()}</span>
            <span className={`timer ${timeLeft <= 10 ? 'critical' : ''}`}>
              {String(Math.floor(timeLeft / 60)).padStart(2, '0')}:{String(timeLeft % 60).padStart(2, '0')}
            </span>
          </div>
        </div>

        {/* Challenge Content */}
        <div className="challenge-content">
          <h2 className="challenge-title">► {challenge.title}_</h2>
          
          <div className="setup-section">
            <p className="setup-text">{challenge.setup}</p>
            
            {challenge.code && <CodeBlock>{challenge.code}</CodeBlock>}
            {challenge.testCases && <TestCasesList testCases={challenge.testCases} />}
            {challenge.requirements && <RequirementsBlock>{challenge.requirements}</RequirementsBlock>}
          </div>

          <div className="question-section">
            <p className="question-text">► {challenge.question}_</p>
          </div>

          <div className="options-grid">
            {challenge.options.map((option, i) => (
              <button
                key={i}
                className={`option-btn ${
                  hasAnswered
                    ? i === challenge.correct
                      ? 'correct'
                      : i === selectedAnswer
                      ? 'incorrect'
                      : 'disabled'
                    : ''
                }`}
                onClick={() => onAnswer(i)}
                disabled={hasAnswered}
              >
                <span className="option-key">[{String.fromCharCode(65 + i)}]</span>
                <span className="option-text">{option}</span>
              </button>
            ))}
          </div>

          {/* AI Hint Section - only show before answering */}
          {!hasAnswered && <AIHintSection challenge={challenge} disabled={hasAnswered} />}

          {/* Next Case Button - shows after answering and dismissing popup */}
          {hasAnswered && hasSeenExplanation && (
            <div className="next-case-section">
              <button className="next-case-btn" onClick={onNext}>
                {currentLevel >= totalLevels - 1 ? '► VIEW RESULTS' : '► NEXT CASE'}
              </button>
            </div>
          )}
        </div>
      </TerminalWindow>

      {/* Popup Alert - Only shows until dismissed */}
      {showPopup && (
        <PopupAlert
          challenge={challenge}
          isCorrect={isCorrect}
          onDismiss={handleDismissPopup}
        />
      )}
    </>
  );
};

/**
 * Game Complete Screen Component
 */
export const CompleteScreen = ({ results, answers, onRestart }) => {
  const { score, correctCount, totalCount, percentage, rank, color } = results;

  return (
    <TerminalWindow title="[ CASE CLOSED ]">
      <div className="complete-screen">
        {/* Clean, readable completion title */}
        <div className="title-section">
          <h1 className="main-title complete">MISSION COMPLETE</h1>
          <div className="title-underline"></div>
        </div>

        <div className="results-grid">
          <div className="result-box">
            <span className="result-label">FINAL SCORE</span>
            <span className="result-value score">{score.toLocaleString()}</span>
          </div>
          <div className="result-box">
            <span className="result-label">CASES SOLVED</span>
            <span className="result-value">{correctCount}/{totalCount}</span>
          </div>
          <div className="result-box">
            <span className="result-label">ACCURACY</span>
            <span className="result-value">{percentage}%</span>
          </div>
        </div>

        <div className="rank-display" style={{ "--rank-color": color }}>
          <span className="rank-label">QA CLEARANCE LEVEL:</span>
          <span className="rank-value"><GlitchText intensity="high">{rank}</GlitchText></span>
        </div>

        <div className="level-summary">
          {answers.map((answer, i) => (
            <span 
              key={i} 
              className={`level-dot ${answer.correct ? 'correct' : 'incorrect'}`}
              title={`Case ${i + 1}: ${answer.correct ? 'Solved' : 'Failed'}`}
            />
          ))}
        </div>

        <div className="complete-actions">
          <button className="action-btn" onClick={onRestart}>
            ► NEW INVESTIGATION
          </button>
          <a href="https://jeffheytow.com" className="action-btn secondary">
            ► RETURN TO HQ
          </a>
        </div>
      </div>
    </TerminalWindow>
  );
};
